import React, { useState, useEffect, useRef } from 'react';
import { AddressRequest, AddressDto } from '../../../../services/api/contracts/address';
import { useGetAddresses, useCreateCustomerAddress, useUpdateAddress, useDeleteAddress } from '../../../../services/api/hooks/address';
import AddressAutocomplete, { AutocompleteAddressData } from './AddressAutocomplete';
import { AddressFormModal } from './AddressFormModal';
import { Button } from '../../Button';
import { ButtonVariant } from '../../buttonHelper';
import { Pencil, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TextInput } from '../text/TextInput';
import { useDirtyFormGuard } from '@/hooks/useDirtyFormGuard';
import { Modal } from '@/components/modal/Modal';
import { toast } from 'sonner';

interface AddressBookSelectorProps {
  mspId: string;
  customerId: string;
  onChange: (payload: Omit<AddressRequest, 'name'>) => void;
  className?: string;
}

export const AddressBookSelector: React.FC<AddressBookSelectorProps> = ({
  mspId,
  customerId,
  onChange,
  className = ''
}) => {
  const [mode, setMode] = useState<'saved' | 'search' | 'manual'>('search');
  const [selectedAddressId, setSelectedAddressId] = useState<string>('');
  
  const [addresses, { isLoading }] = useGetAddresses(mspId, customerId);
  const createCustomerAddress = useCreateCustomerAddress();
  const updateAddress = useUpdateAddress();
  const deleteAddress = useDeleteAddress();

  const [currentSearchAddress, setCurrentSearchAddress] = useState<AutocompleteAddressData | null>(null);

  // For AddressFormModal (Edit)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [addressToEdit, setAddressToEdit] = useState<AddressDto | null>(null);

  // For Delete Confirmation Modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState<AddressDto | null>(null);

  // For Accordion UX
  const [expandedAddressId, setExpandedAddressId] = useState<string | null>(null);

  const lastEmittedPayload = useRef<string>('');

  // Manual Form
  const { control: manualControl, watch: manualWatch, formState: { isDirty: isManualDirty, isValid: isManualValid }, getValues: getManualValues, reset: resetManual } = useForm<AddressRequest>({
    resolver: zodResolver(AddressRequest),
    mode: 'onChange',
    defaultValues: {
      name: '',
      line1: '',
      line2: '',
      city: '',
      state: '',
      postcode: '',
      country: 'UK',
      saveToAddressBook: false
    }
  });

  useDirtyFormGuard(isManualDirty && mode === 'manual');

  const emitChange = (payload: Omit<AddressRequest, 'name'>) => {
    const payloadString = JSON.stringify(payload);
    if (lastEmittedPayload.current !== payloadString) {
      lastEmittedPayload.current = payloadString;
      onChange(payload);
    }
  };

  // Emit on saved selection change
  useEffect(() => {
    if (mode === 'saved' && selectedAddressId && addresses) {
      const addr = addresses.find((a: AddressDto) => a.id === selectedAddressId);
      if (addr) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, name, ...rest } = addr;
        emitChange({
          ...rest,
          companyId: customerId,
          partnerId: mspId
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedAddressId, addresses, customerId, mspId]);

  // Emit on manual form change
  useEffect(() => {
    const subscription = manualWatch((value) => {
      if (mode === 'manual') {
        const { name, saveToAddressBook, ...rest } = value;
        // Even if invalid, we might want to emit to let parent validation fail, but better to emit full object
        emitChange({
          ...rest,
          companyId: saveToAddressBook ? customerId : null,
          partnerId: mspId,
          saveToAddressBook: saveToAddressBook
        } as any);
      }
    });
    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualWatch, mode, customerId, mspId]);

  const handleAutocompleteChange = (addressData: AutocompleteAddressData) => {
    setCurrentSearchAddress(addressData);
    if (mode === 'search') {
      emitChange({
        ...addressData,
        companyId: null,
        partnerId: mspId
      });
    }
  };

  const handleEditSaved = (addr: AddressDto) => {
    setAddressToEdit(addr);
    setIsEditModalOpen(true);
  };

  const handleDeleteRequest = (addr: AddressDto) => {
    setAddressToDelete(addr);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!addressToDelete) return;
    try {
      await deleteAddress.mutateAsync({ id: addressToDelete.id, mspId, customerId });
      toast.success('Address deleted successfully');
      if (selectedAddressId === addressToDelete.id) {
        setSelectedAddressId('');
      }
      setIsDeleteModalOpen(false);
      setAddressToDelete(null);
    } catch (e) {
      toast.error('Failed to delete address');
    }
  };

  const onEditSave = async (payload: AddressRequest) => {
    if (!addressToEdit) return;
    try {
      await updateAddress.mutateAsync({
        ...payload,
        id: addressToEdit.id,
        mspId,
        customerId
      });
      toast.success('Address updated successfully');
      setIsEditModalOpen(false);
      setAddressToEdit(null);
    } catch (e) {
      toast.error('Failed to update address');
      throw e;
    }
  };

  return (
    <div className={`flex flex-col space-y-4 ${className}`}>
      {/* Mode Toggle */}
      <div className="flex flex-wrap gap-4 items-center">
        <label className="flex items-center space-x-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
          <input
            type="radio"
            className="text-primary-600 focus:ring-primary-500 h-4 w-4 border-slate-300"
            checked={mode === 'search'}
            onChange={() => setMode('search')}
          />
          <span>Search</span>
        </label>
        <label className="flex items-center space-x-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
          <input
            type="radio"
            className="text-primary-600 focus:ring-primary-500 h-4 w-4 border-slate-300"
            checked={mode === 'saved'}
            onChange={() => setMode('saved')}
          />
          <span>Use Saved</span>
        </label>
        <label className="flex items-center space-x-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
          <input
            type="radio"
            className="text-primary-600 focus:ring-primary-500 h-4 w-4 border-slate-300"
            checked={mode === 'manual'}
            onChange={() => {
              setMode('manual');
              resetManual();
            }}
          />
          <span>Manually Enter</span>
        </label>
      </div>

      {/* Render based on Mode */}
      {mode === 'saved' && (
        <div className="space-y-3">
          {isLoading ? (
            <p className="text-sm text-slate-500">Loading addresses...</p>
          ) : addresses && addresses.length > 0 ? (
            <div className="flex flex-col space-y-2 max-h-60 overflow-y-auto p-1">
              {addresses.map((addr: AddressDto) => {
                const isExpanded = expandedAddressId === addr.id;
                const isSelected = selectedAddressId === addr.id;

                return (
                  <div 
                    key={addr.id}
                    className={`relative rounded-lg border transition-all ${
                      isSelected 
                        ? 'border-primary-500 ring-1 ring-primary-500 bg-white dark:bg-slate-800' 
                        : 'border-slate-200 dark:border-slate-700 hover:border-primary-300 bg-white dark:bg-slate-800'
                    }`}
                  >
                    {/* Header (Summary) */}
                    <div 
                      className="flex justify-between items-center p-3 cursor-pointer"
                      onClick={() => setSelectedAddressId(addr.id)}
                    >
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <input
                          type="radio"
                          className="text-primary-600 focus:ring-primary-500 h-4 w-4 border-slate-300 shrink-0 cursor-pointer"
                          checked={isSelected}
                          readOnly
                        />
                        <div className="truncate text-sm text-slate-900 dark:text-slate-100">
                          <span className="font-semibold mr-1">{addr.name || 'Saved Address'}</span>
                          <span className="text-slate-500 dark:text-slate-400">- {addr.line1}, {addr.city}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1 ml-4 shrink-0">
                        <button 
                          type="button" 
                          onClick={(e) => { e.stopPropagation(); handleEditSaved(addr); }}
                          className="p-1.5 text-slate-400 hover:text-primary-600 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button 
                          type="button" 
                          onClick={(e) => { e.stopPropagation(); handleDeleteRequest(addr); }}
                          className="p-1.5 text-slate-400 hover:text-red-600 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setExpandedAddressId(isExpanded ? null : addr.id); 
                          }}
                          className="p-1.5 ml-1 text-slate-500 hover:text-slate-700 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Panel (Expanded View) */}
                    {isExpanded && (
                      <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 rounded-b-lg">
                        <div className="text-xs text-slate-600 dark:text-slate-400 space-y-1 ml-7">
                          <p>{addr.line1}</p>
                          {addr.line2 && <p>{addr.line2}</p>}
                          <p>{addr.city}{addr.state ? `, ${addr.state}` : ''}</p>
                          <p>{addr.postcode}</p>
                          <p>{addr.country}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No saved addresses found. Please search or manually enter one.</p>
          )}
        </div>
      )}

      {mode === 'search' && (
        <div className="flex flex-col space-y-3 p-4 border border-slate-200 dark:border-slate-800 rounded-md bg-slate-50 dark:bg-slate-800/50">
          <AddressAutocomplete 
            onChange={handleAutocompleteChange} 
          />
          
          {currentSearchAddress && (
            <div className="mt-4 p-4 bg-white dark:bg-slate-900 rounded-md border border-slate-200 dark:border-slate-700">
              <div className="text-sm text-slate-700 dark:text-slate-300">
                <p className="font-semibold mb-1">Selected Address:</p>
                <p>{currentSearchAddress.line1}</p>
                {currentSearchAddress.line2 && <p>{currentSearchAddress.line2}</p>}
                <p>{currentSearchAddress.city}{currentSearchAddress.state ? `, ${currentSearchAddress.state}` : ''}</p>
                <p>{currentSearchAddress.postcode}</p>
                <p>{currentSearchAddress.country}</p>
              </div>

              <div className="pt-4 mt-4 border-t border-slate-200 dark:border-slate-700">
                <Controller
                  name="saveToAddressBook"
                  control={manualControl}
                  render={({ field }) => (
                    <label className="flex items-center space-x-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                      <input
                        type="checkbox"
                        className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                        checked={field.value || false}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          // Re-emit search address with save flag
                          emitChange({
                            ...currentSearchAddress,
                            companyId: e.target.checked ? customerId : null,
                            partnerId: mspId,
                            saveToAddressBook: e.target.checked
                          } as any);
                        }}
                      />
                      <span>Save to Address Book</span>
                    </label>
                  )}
                />
                {manualWatch('saveToAddressBook') && (
                  <div className="mt-3">
                    <Controller
                      name="name"
                      control={manualControl}
                      render={({ field }) => (
                        <div>
                          <label className="block text-sm font-medium mb-1">Location Name / Alias</label>
                          <TextInput 
                            id="search-save-name" 
                            value={field.value} 
                            onChange={(val: string) => {
                              field.onChange(val);
                              emitChange({
                                ...currentSearchAddress,
                                name: val,
                                companyId: customerId,
                                partnerId: mspId,
                                saveToAddressBook: true
                              } as any);
                            }} 
                            placeholder="e.g. Headquarters"
                          />
                        </div>
                      )}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {mode === 'manual' && (
        <div className="p-4 border border-slate-200 dark:border-slate-800 rounded-md bg-slate-50 dark:bg-slate-800/50 space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <Controller
              name="line1"
              control={manualControl}
              render={({ field }) => (
                <div>
                  <label className="block text-sm font-medium mb-1">Line 1</label>
                  <TextInput id="manual-line1" value={field.value} onChange={field.onChange} />
                </div>
              )}
            />
            <Controller
              name="line2"
              control={manualControl}
              render={({ field }) => (
                <div>
                  <label className="block text-sm font-medium mb-1">Line 2</label>
                  <TextInput id="manual-line2" value={field.value || ''} onChange={field.onChange} />
                </div>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <Controller
                name="city"
                control={manualControl}
                render={({ field }) => (
                  <div>
                    <label className="block text-sm font-medium mb-1">City</label>
                    <TextInput id="manual-city" value={field.value} onChange={field.onChange} />
                  </div>
                )}
              />
              <Controller
                name="state"
                control={manualControl}
                render={({ field }) => (
                  <div>
                    <label className="block text-sm font-medium mb-1">State / Province</label>
                    <TextInput id="manual-state" value={field.value || ''} onChange={field.onChange} />
                  </div>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Controller
                name="postcode"
                control={manualControl}
                render={({ field }) => (
                  <div>
                    <label className="block text-sm font-medium mb-1">Postcode</label>
                    <TextInput id="manual-postcode" value={field.value} onChange={field.onChange} />
                  </div>
                )}
              />
              <Controller
                name="country"
                control={manualControl}
                render={({ field }) => (
                  <div>
                    <label className="block text-sm font-medium mb-1">Country</label>
                    <TextInput id="manual-country" value={field.value || ''} onChange={field.onChange} />
                  </div>
                )}
              />
            </div>
          </div>

          <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
            <Controller
              name="saveToAddressBook"
              control={manualControl}
              render={({ field }) => (
                <label className="flex items-center space-x-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                    checked={field.value || false}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                  <span>Save to Address Book</span>
                </label>
              )}
            />
            {manualWatch('saveToAddressBook') && (
              <div className="mt-3">
                <Controller
                  name="name"
                  control={manualControl}
                  render={({ field }) => (
                    <div>
                      <label className="block text-sm font-medium mb-1">Location Name / Alias</label>
                      <TextInput 
                        id="manual-name" 
                        value={field.value} 
                        onChange={field.onChange} 
                        placeholder="e.g. Headquarters"
                      />
                    </div>
                  )}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {isEditModalOpen && addressToEdit && (
        <AddressFormModal 
          isOpen={isEditModalOpen}
          onClose={() => { setIsEditModalOpen(false); setAddressToEdit(null); }}
          mode="edit"
          initialData={addressToEdit}
          onSave={onEditSave}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} open={() => {}} close={() => setIsDeleteModalOpen(false)} className="max-w-md bg-white p-6 rounded-lg">
        <Modal.Controls close={() => setIsDeleteModalOpen(false)} isOpen={isDeleteModalOpen} open={() => {}}>
          <h2 className="text-lg font-semibold">Delete Address</h2>
        </Modal.Controls>
        <div className="py-4 text-slate-600 dark:text-slate-300">
          Are you sure you want to delete the address <strong>{addressToDelete?.name || 'Saved Address'}</strong>? This action cannot be undone.
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button type="button" onClick={() => setIsDeleteModalOpen(false)}>Cancel</Button>
          <Button type="button" onClick={handleDeleteConfirm}>Delete Address</Button>
        </div>
      </Modal>
    </div>
  );
};

export default AddressBookSelector;
